import React from "react";
import {twJoin} from "tailwind-merge";
import {FormattedMessage, FormattedNumber, FormattedTime, useIntl} from "react-intl";
import {CalendarDaysIcon, ClockIcon, ShoppingBagIcon, TruckIcon} from "@heroicons/react/16/solid";
import {PosOrderSummary} from "@/data/entities/order";
import PaymentTag from "@widgets/pos/orders/sidebar/list/PaymentTag";
import {Badge} from "peggirkit";
import BadgeIconIndicator from "peggirkit/dist/components/Badge/BadgeElements/Indicator/BadgeIconIndicator";
import RequestedTime from "@widgets/pos/orders/details/order/overview/header/RequestedTime";
import dayjs from "dayjs";

type Props = {
    order: PosOrderSummary,
    selected: boolean,
    onSelect: () => void,
};

const OrderListItem = ({order, selected, onSelect}: Props) => {
    const intl = useIntl();
    const isCancelled = order.cancelledOn !== null;
    const dim = isCancelled ? "opacity-55" : undefined;
    const isInAdvance: boolean = order.fulfillmentDate !== dayjs(order.creationDate).format("YYYY-MM-DD"); // Placed on an earlier day for this day

    return (
        <button
            type={"button"}
            onClick={onSelect}
            aria-pressed={selected}
            aria-label={intl.formatMessage({id: "pos.order.open"}, {number: order.cartId})}
            className={twJoin(
                "w-full flex items-stretch gap-3 p-3 text-left rounded-lg shadow-sm transition-colors",
                "bg-white hover:bg-gray-50 dark:bg-slate-800 dark:hover:bg-slate-700/60",
                selected
                    ? "ring-2 ring-blue-600 dark:ring-blue-400"
                    : "ring-1 ring-gray-900/10 dark:ring-white/10",
            )}
        >
            {/* Requested time */}
            <div
                className={twJoin(
                    "w-[76px] shrink-0 rounded-md px-1 py-2 flex flex-col items-center justify-center gap-0.5",
                    "bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-white",
                    dim,
                )}
            >
                <p className={"text-[11px] font-semibold uppercase tracking-wide opacity-75"}>
                    <FormattedMessage id={"pos.order.requested"}/>
                </p>
                <p className={"font-bold leading-tight tabular-nums"}>
                    <RequestedTime order={order} withDate={false}/>
                </p>
            </div>

            <div className={"flex-1 min-w-0 flex flex-col justify-between gap-1.5"}>
                {/* Order number & fulfillment */}
                <div className={"flex items-center gap-2"}>
                    <p className={"text-lg font-bold tabular-nums text-gray-900 dark:text-white"}>
                        #{order.cartId}
                    </p>
                    <span className={"flex-1"}/>
                    {isInAdvance && (
                        <Badge
                            color={"warning"}
                            size={"xs"}
                            indicator={<BadgeIconIndicator icon={CalendarDaysIcon}/>}
                        >
                            <FormattedMessage id={"pos.order.inAdvance"}/>
                        </Badge>
                    )}
                    <Badge
                        color={"primary"}
                        size={"xs"}
                        indicator={<BadgeIconIndicator icon={order.delivery ? TruckIcon : ShoppingBagIcon}/>}
                    >
                        <FormattedMessage id={order.delivery ? "delivery" : "takeaway"}/>
                    </Badge>
                </div>

                {/* Name & total */}
                <div className={twJoin("flex items-baseline gap-2 text-[15px] text-gray-900 dark:text-white", dim)}>
                    <p className={"flex-1 min-w-0 truncate text-sm"}>
                        {order.firstName} {order.lastName}
                    </p>
                    <p className={twJoin("font-semibold tabular-nums", isCancelled && "line-through")}>
                        <FormattedNumber value={order.totalToPay} style={"currency"} currency={"EUR"}/>
                    </p>
                </div>

                {/* Placed at & payment */}
                <div className={"flex items-center gap-2"}>
                    {order.creationDate !== null && (
                        <p
                            className={twJoin(
                                "inline-flex items-center gap-1 text-xs tabular-nums whitespace-nowrap text-gray-600 dark:text-slate-400",
                                dim,
                            )}
                        >
                            <ClockIcon className={"size-4"}/>
                            <FormattedMessage
                                id={"pos.order.placedAt"}
                                values={{
                                    time: <FormattedTime
                                        value={order.creationDate}
                                        hour={"2-digit"}
                                        minute={"2-digit"}
                                        hourCycle={"h23"}
                                    />,
                                }}
                            />
                        </p>
                    )}

                    <span className={"flex-1"}/>

                    <PaymentTag order={order}/>
                </div>
            </div>
        </button>
    );
};

export default OrderListItem;